import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from "date-fns";
import { Plus, Video } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { formatDateTime } from "@/utils/formatDate";
import { MeetingsCalendar } from "../components/MeetingsCalendar";
import { MeetingFormModal } from "../components/MeetingFormModal";
import { OccurrenceStatusBadge } from "../components/OccurrenceStatusBadge";
import { useCreateMeeting, useOccurrencesRange } from "../hooks/useMeetings";
import type { MeetingDetail, Occurrence } from "../types";

function initialRange() {
  const now = new Date();
  return {
    from: format(startOfWeek(startOfMonth(now), { weekStartsOn: 1 }), "yyyy-MM-dd"),
    to: format(endOfWeek(endOfMonth(now), { weekStartsOn: 1 }), "yyyy-MM-dd"),
  };
}

export function MeetingsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isInternal = user?.role !== "contractor";

  const [range, setRange] = useState(initialRange);
  const [formOpen, setFormOpen] = useState(false);
  const { data: occurrences = [] } = useOccurrencesRange(range);

  const upcoming = useMemo(() => {
    const now = Date.now();
    return [...occurrences]
      .filter(
        (o) =>
          (o.status === "scheduled" || o.status === "live") &&
          new Date(o.ends_at).getTime() > now,
      )
      .sort((a, b) => a.starts_at.localeCompare(b.starts_at))
      .slice(0, 8);
  }, [occurrences]);

  const createInstant = useCreateMeeting();

  const startInstant = async () => {
    try {
      const created: MeetingDetail = await createInstant.mutateAsync({
        title: `Spotkanie ${user?.first_name ?? ""} ${formatDateTime(new Date())}`.trim(),
        kind: "instant",
        participant_ids: [],
      });
      if (created.occurrence) navigate(`/meetings/room/${created.occurrence.id}`);
    } catch {
      /* error toast could go here */
    }
  };

  const openOccurrence = (occ: Occurrence) =>
    navigate(`/meetings/occurrences/${occ.id}`);

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-bold text-[var(--color-text-primary)]">
          Spotkania zdalne
        </h1>
        {isInternal && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void startInstant()}
              disabled={createInstant.isPending}
              className="flex items-center gap-1.5 rounded-md border border-[var(--color-primary)] px-3 py-1.5 text-sm font-medium text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white disabled:opacity-50"
            >
              <Video size={15} /> Rozpocznij teraz
            </button>
            <button
              type="button"
              onClick={() => setFormOpen(true)}
              className="flex items-center gap-1.5 rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
            >
              <Plus size={15} /> Zaplanuj spotkanie
            </button>
          </div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_18rem]">
        <MeetingsCalendar
          occurrences={occurrences}
          onRangeChange={(from, to) => setRange({ from, to })}
          onPickOccurrence={openOccurrence}
        />

        <aside className="space-y-2">
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
            Nadchodzące
          </h2>
          {upcoming.length === 0 && (
            <p className="text-sm text-[var(--color-text-muted)]">
              Brak nadchodzących spotkań.
            </p>
          )}
          {upcoming.map((occ) => (
            <button
              key={occ.id}
              type="button"
              onClick={() => openOccurrence(occ)}
              className="block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-2.5 text-left hover:border-[var(--color-primary)]"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium text-[var(--color-text-primary)]">
                  {occ.title}
                </span>
                <OccurrenceStatusBadge status={occ.status} />
              </div>
              <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                {new Date(occ.starts_at).toLocaleString("pl-PL", {
                  weekday: "short",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                {occ.organizer ? ` · ${occ.organizer.name}` : ""}
              </div>
            </button>
          ))}
        </aside>
      </div>

      <MeetingFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onCreated={(m) => {
          if (m.occurrence) navigate(`/meetings/occurrences/${m.occurrence.id}`);
        }}
      />
    </div>
  );
}
